import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppLayout } from '../../layouts';
import { useAppSelector } from '../../stores';
import CreditsService from '../../services/api/creditsService';
import { formatLongDate } from '../../utils/formatDate';

// Styles CSS pour les animations
const animationStyles = `
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
  
  @keyframes sparkle {
    0%, 100% { opacity: 0; transform: scale(0.5); }
    50% { opacity: 1; transform: scale(1); }
  }
  
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

/**
 * PaymentSuccessView - Page de confirmation après paiement Stripe réussi
 * 
 * Cette page est affichée après redirection depuis Stripe Checkout.
 * Elle récupère les paramètres URL et affiche une confirmation adaptée au type de paiement.
 * 
 * URL attendue : /payment-success?payment=success&plan=ESSENTIAL
 */
const PaymentSuccessView: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const auth = useAppSelector((state) => state.auth);
    const [loading, setLoading] = useState(true);
    const [creditBalance, setCreditBalance] = useState<any>(null);

    // Paramètres de l'URL
    const plan = searchParams.get('plan');
    const isTopup = searchParams.get('topup') === 'true';

    useEffect(() => {
        // Récupérer les nouveaux crédits après paiement
        const loadUpdatedBalance = async () => {
            try {
                if (auth.access_token && auth.session.company_id) {
                    const response = await CreditsService.getCreditBalance(
                        auth.session.company_id,
                        auth.access_token
                    );
                    
                    if (response.success && response.data) {
                        setCreditBalance(response.data);
                    }
                }
            } catch (error) {
                console.error('[PaymentSuccess] Error loading credit balance:', error);
            } finally {
                setLoading(false);
            }
        };

        // Petite attente pour laisser le webhook traiter le paiement
        const timer = setTimeout(() => {
            loadUpdatedBalance();
        }, 2000);

        return () => clearTimeout(timer);
    }, [auth.access_token, auth.session.company_id]);

    const getPlanDetails = () => {
        switch (plan) {
            case 'ESSENTIAL':
                return {
                    name: 'Essential',
                    emoji: '⭐',
                    color: 'warning',
                    description: isTopup 
                        ? t('views.payment.success.planDescriptions.topupEssential')
                        : t('views.payment.success.planDescriptions.essential')
                };
            case 'PLUS':
                return {
                    name: 'Plus',
                    emoji: '💎',
                    color: 'primary',
                    description: t('views.payment.success.planDescriptions.plus')
                };
            default:
                return {
                    name: 'Premium',
                    emoji: '✅',
                    color: 'success',
                    description: t('views.payment.success.planDescriptions.default')
                };
        }
    };

    const planDetails = getPlanDetails();

    return (
        <AppLayout>
            {/* Injection des styles d'animation */}
            <style>{animationStyles}</style>
            
            <div className="container-fluid d-flex align-items-center justify-content-center py-5" style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)' }}>
                <div className="card border-0 shadow-lg" style={{ maxWidth: '650px', background: 'linear-gradient(145deg, #2a2a2a, #1e1e1e)', borderRadius: '20px' }}>
                    <div className="card-body text-center p-5">
                        {/* Animation de succès améliorée */}
                        <div className="mb-4 position-relative">
                            <div 
                                className={`rounded-circle mx-auto d-flex align-items-center justify-content-center position-relative`}
                                style={{ 
                                    width: '100px', 
                                    height: '100px',
                                    background: `linear-gradient(135deg, ${planDetails.color === 'primary' ? '#007bff, #0056b3' : planDetails.color === 'warning' ? '#ffc107, #e0a800' : '#28a745, #1e7e34'})`,
                                    boxShadow: '0 10px 30px rgba(0,123,255,0.3)',
                                    animation: 'pulse 2s infinite'
                                }}
                            >
                                <i className="fa-solid fa-check text-white" style={{ fontSize: '2.5rem' }}></i>
                            </div>
                            {/* Particules d'animation */}
                            <div className="position-absolute top-0 start-0 w-100 h-100">
                                {[...Array(6)].map((_, i) => (
                                    <div
                                        key={i}
                                        className="position-absolute bg-warning rounded-circle"
                                        style={{
                                            width: '6px',
                                            height: '6px',
                                            top: `${20 + Math.sin(i) * 30}%`,
                                            left: `${20 + Math.cos(i) * 30}%`,
                                            animation: `sparkle 2s infinite ${i * 0.2}s`
                                        }}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Titre avec gradient */}
                        <h1 className="mb-3" style={{
                            background: 'linear-gradient(45deg, #ffd700, #ffed4e)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontSize: '2.5rem',
                            fontWeight: 'bold'
                        }}>
                            {planDetails.emoji} {t('views.payment.success.title')}
                        </h1>

                        {/* Description avec meilleure typographie */}
                        <p className="text-light fs-5 mb-4 fw-light" style={{ lineHeight: '1.6' }}>
                            {planDetails.description}
                        </p>

                        {/* Détails des crédits améliorés */}
                        {loading ? (
                            <div className="d-flex align-items-center justify-content-center mb-4 p-4 rounded-3" style={{ background: 'rgba(255,255,255,0.1)' }}>
                                <div className="spinner-border text-primary me-3" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <span className="text-white fs-6">{t('views.payment.success.loading')}</span>
                            </div>
                        ) : creditBalance ? (
                            <div className="mb-4">
                                {/* Carte principale des crédits */}
                                <div 
                                    className="p-4 rounded-4 position-relative overflow-hidden"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(13, 110, 253, 0.1), rgba(13, 202, 240, 0.1))',
                                        border: '1px solid rgba(13, 110, 253, 0.2)',
                                        backdropFilter: 'blur(10px)'
                                    }}
                                >
                                    {/* Effet de brillance */}
                                    <div 
                                        className="position-absolute top-0 start-0 w-100 h-100"
                                        style={{
                                            background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
                                            animation: 'shimmer 3s infinite'
                                        }}
                                    />
                                    
                                    <h6 className="mb-3 d-flex align-items-center" style={{ color: '#ffd700' }}>
                                        <i className="fa-solid fa-coins me-2 fs-5"></i>
                                        <span className="fw-bold">{t('views.payment.success.newBalance')}</span>
                                    </h6>
                                    
                                    {/* Plan avec badge stylé */}
                                    <div className="row align-items-center mb-3">
                                        <div className="col-6">
                                            <span className="text-light fw-medium">{t('views.payment.success.plan')}:</span>
                                        </div>
                                        <div className="col-6 text-end">
                                            <span 
                                                className="badge px-3 py-2 fs-6 fw-bold rounded-pill"
                                                style={{
                                                    background: planDetails.color === 'primary' ? 'linear-gradient(45deg, #007bff, #0056b3)' : 
                                                               planDetails.color === 'warning' ? 'linear-gradient(45deg, #ffc107, #e0a800)' : 
                                                               'linear-gradient(45deg, #28a745, #1e7e34)',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '1px'
                                                }}
                                            >
                                                {CreditsService.formatPlanName(creditBalance.plan)}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    {/* Crédits disponibles avec compteur animé */}
                                    <div className="row align-items-center mb-3">
                                        <div className="col-6">
                                            <span className="text-light fw-medium">{t('views.payment.success.creditsAvailable')}:</span>
                                        </div>
                                        <div className="col-6 text-end">
                                            <span 
                                                className="fw-bold fs-3"
                                                style={{
                                                    background: 'linear-gradient(45deg, #28a745, #20c997)',
                                                    WebkitBackgroundClip: 'text',
                                                    WebkitTextFillColor: 'transparent',
                                                    fontFamily: 'monospace'
                                                }}
                                            >
                                                {creditBalance.credits_available}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    {/* Prochain prélèvement pour PLUS */}
                                    {creditBalance.plan === 'PLUS' && (
                                        <div className="row align-items-center">
                                            <div className="col-6">
                                                <span className="text-light fw-medium">{t('views.payment.success.nextBilling')}:</span>
                                            </div>
                                            <div className="col-6 text-end">
                                                <span className="text-warning fw-bold">
                                                    {creditBalance.next_billing_date 
                                                        ? formatLongDate(creditBalance.next_billing_date)
                                                        : 'N/A'
                                                    }
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : null}

                        {/* Messages spéciaux selon le plan */}
                        {plan === 'PLUS' && (
                            <div 
                                className="p-4 rounded-3 mb-4 border-0"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(13, 110, 253, 0.15), rgba(102, 16, 242, 0.15))',
                                    border: '1px solid rgba(13, 110, 253, 0.3) !important'
                                }}
                            >
                                <div className="d-flex align-items-start">
                                    <i className="fa-solid fa-info-circle me-3 mt-1" style={{ color: '#ffd700', fontSize: '1.25rem' }}></i>
                                    <div>
                                        <p className="mb-0 text-light" style={{ lineHeight: '1.5' }}>
                                            {t('views.payment.success.plusInfo')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {isTopup && (
                            <div 
                                className="p-4 rounded-3 mb-4 border-0"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(40, 167, 69, 0.15), rgba(32, 201, 151, 0.15))',
                                    border: '1px solid rgba(40, 167, 69, 0.3) !important'
                                }}
                            >
                                <div className="d-flex align-items-start">
                                    <i className="fa-solid fa-plus-circle me-3 mt-1" style={{ color: '#20c997', fontSize: '1.25rem' }}></i>
                                    <div>
                                        <p className="mb-0 text-light" style={{ lineHeight: '1.5' }}>
                                            {t('views.payment.success.topupInfo')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Actions avec design moderne */}
                        <div className="d-flex gap-3 justify-content-center flex-wrap mt-4">
                            <button
                                className="btn px-5 py-3 border-0 rounded-pill position-relative overflow-hidden"
                                onClick={() => navigate('/dashboard/home')}
                                style={{
                                    background: 'linear-gradient(45deg, #007bff, #0056b3)',
                                    color: 'white',
                                    fontWeight: '600',
                                    fontSize: '1.1rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 8px 25px rgba(0,123,255,0.3)',
                                    minWidth: '200px'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 12px 35px rgba(0,123,255,0.4)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,123,255,0.3)';
                                }}
                            >
                                <i className="fa-solid fa-tachometer-alt me-2"></i>
                                {t('views.payment.success.gotoDashboard')}
                            </button>
                            
                            <button
                                className="btn px-5 py-3 border rounded-pill position-relative overflow-hidden"
                                onClick={() => navigate('/process/create')}
                                style={{
                                    background: 'transparent',
                                    color: '#ffd700',
                                    fontWeight: '600',
                                    fontSize: '1.1rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    borderColor: '#ffd700',
                                    transition: 'all 0.3s ease',
                                    minWidth: '200px'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#ffd700';
                                    e.currentTarget.style.color = '#000';
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'transparent';
                                    e.currentTarget.style.color = '#ffd700';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                            >
                                <i className="fa-solid fa-plus me-2"></i>
                                {t('views.payment.success.createProcess')}
                            </button>
                        </div>

                        {/* Footer */}
                        <div className="mt-4 pt-4 border-top border-secondary">
                            <p className="text-muted small mb-0">
                                <i className="fa-solid fa-shield-alt me-2"></i>
                                {t('views.payment.success.security')}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
};

export default PaymentSuccessView;
